import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Button from '@teladoc/pulse/ui/Button';
import Form from '@teladoc/pulse/ui/Form';
import HorizontalRule from '@teladoc/pulse/ui/HorizontalRule';
import Image from '@teladoc/pulse/ui/Image';
import Label from '@teladoc/pulse/ui/Label';
import TextArea from '@teladoc/pulse/ui/TextArea';
import Arg from '@livongo/arg';
import useTransLoader from 'i18n/use-trans-loader';
import MixpanelUtils from '../common/mix-panel';
import AcuityError from '../ErrorMessages/AcuityError';
import AmpliUtils from '../utils/amplitude-utils';
import {TRACK_EVENT_KEYS} from '../constants/amplitude';
import SharedCoaching from '../utils/SharedCoaching';
import css from './SchedulingForm.scss';
import SharedCoachingDate from './SharedCoachingDate';

const SharedCoachingSchedulingForm = () => {
    const {t} = useTransLoader('schedulingForm');
    const navigate = useNavigate();
    const {sharedCoaching} = useSelector(state => state.scheduler);
    const {pid} = useSelector(state => state.userData.userData);
    const {token, uuid} = useSelector(state => state.userData.auth);
    const [disableScheduleButton, setDisableScheduleButton] = useState(false);
    const [APIError, setAPIError] = useState();
    const [notes, setNotes] = useState('');
    const oneApp =
        Arg('oneapp') ||
        Arg('ccmOneApp') ||
        window.location.hostname.includes('teladoc');

    const scheduleAppointment = () => {
        setDisableScheduleButton(true);

        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.SCHEDULE_BUTTON_CLICKED, {
            [TRACK_EVENT_KEYS.HasNotes]: notes?.trim()?.length > 0,
            [TRACK_EVENT_KEYS.IsSharedCoaching]: true,
        });

        SharedCoaching.submitSharedCoaching(
            {
                ...sharedCoaching,
                reason: 1,
                message: notes,
            },
            uuid,
            token
        ).then(() => {
            navigate('/confirmation');
        });

        // Check if user has schedule a coaching session before.
        SharedCoaching.getLaunchpadStatus(token)
            .then(({items}) => {
                const coachStatus = items?.find(
                    item => item.name === 'COACHING'
                );

                // User has not schedule a session
                if (!coachStatus.completed) {
                    const markItem = {
                        item: {name: 'COACHING', completed: true},
                    };

                    SharedCoaching.markLaunchpadStatus(token, markItem);
                }
            })
            .catch(err => {
                // silent
            });
    };

    useEffect(() => {
        if (APIError) {
            MixpanelUtils.track({
                event: 'coaching.scheduling.error',
                properties: {
                    'Error Code': APIError.status,
                },
            });

            AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.SCHEDULING_ERROR, {
                [TRACK_EVENT_KEYS.ErrorCode]: APIError?.status,
                [TRACK_EVENT_KEYS.ErrorDetails]:
                    APIError?.data?.details || 'Unknown error',
            });
        }
    }, [APIError]);

    useEffect(() => {
        MixpanelUtils.identify(pid);
        MixpanelUtils.track({
            event: 'coaching.scheduling.viewed',
            properties: {
                schedulingForm: 'CCM-Shared Coaching',
            },
        });
    }, [pid]);

    return (
        <div>
            <AcuityError isOpen={APIError} toggleModal={setAPIError} />
            <Form className={css.root} onSubmit={() => scheduleAppointment()}>
                <div className={css.header}>
                    <div className={css.logoWrapper}>
                        <Image
                            alt="Teladoc logo"
                            cloudinaryImageId={
                                oneApp
                                    ? 'coach/teladoc_health_logo_ukf9ne'
                                    : 'coach/livongo_by_teladoc_logo_t4rntd'
                            }
                            format="jpg"
                            width={140}
                            height={40}
                        />
                    </div>
                </div>
                <h2>{t('title')}</h2>
                <SharedCoachingDate />
                <HorizontalRule spaceTop={24} spaceBottom={24} />
                <TextArea
                    id="note"
                    name="note"
                    maxLength={255}
                    showCounter
                    placeholder={t('sessionInfo.notesPlaceholder')}
                    label={
                        <Label className={css.sectionTitle}>
                            {t('sessionInfo.notes')}
                        </Label>
                    }
                    onChange={e => setNotes(e.target.value)}
                />
                <Button
                    type="submit"
                    variant="primary"
                    className={css.confirmButton}
                    disabled={disableScheduleButton}
                >
                    {t('sessionInfo.submit')}
                </Button>
            </Form>
        </div>
    );
};

export default SharedCoachingSchedulingForm;
